import {
  HOME_ACTIONS,
  HOME_AUDIENCES,
  HOME_DEFAULT_AUDIENCE,
  HOME_DEFAULT_ICON,
  HOME_ICONS,
  HOME_LINK_PATTERN,
  HOME_RESERVED_OPTION_ID,
  WIDGET_HOME_LIMITS,
} from "@sagot/constants";
import { z } from "zod";
import type { WidgetDraftIn, WidgetOut } from "../shared/types";

const L = WIDGET_HOME_LIMITS;
const ID_SLUG_MAX = 30;

/** Error codes the Home form reports (looked up under `deployKnowledge.widgetStudio.home.errors`). */
const ERROR_CODES = ["labelRequired", "workflowRequired", "bookingRequired", "questionRequired", "urlInvalid", "tooLong", "invalid"] as const;
export type HomeErrorCode = (typeof ERROR_CODES)[number];
const isErrorCode = (message: string): message is HomeErrorCode => ERROR_CODES.some((code) => code === message);

/** One option as the form edits it: every field present (blank when the action doesn't use it). */
export const homeOptionSchema = z
  .object({
    id: z.string().min(1).max(L.optionId),
    icon: z.enum(HOME_ICONS),
    label: z.string().trim().min(1, "labelRequired").max(L.label, "tooLong"),
    description: z.string().trim().max(L.description, "tooLong"),
    action: z.enum(HOME_ACTIONS),
    workflow_id: z.string(),
    action_id: z.string(),
    question: z.string().trim().max(L.question, "tooLong"),
    url: z.string().trim().max(L.url, "tooLong"),
    audience: z.enum(HOME_AUDIENCES),
  })
  .superRefine((option, context) => {
    const missing = (path: "workflow_id" | "action_id" | "question", message: HomeErrorCode) => {
      if (!option[path].trim()) context.addIssue({ code: "custom", path: [path], message });
    };
    if (option.action === "workflow") missing("workflow_id", "workflowRequired");
    if (option.action === "booking") missing("action_id", "bookingRequired");
    if (option.action === "question") missing("question", "questionRequired");
    if (option.action === "link" && !HOME_LINK_PATTERN.test(option.url.trim())) context.addIssue({ code: "custom", path: ["url"], message: "urlInvalid" });
  });
export type HomeOptionForm = z.infer<typeof homeOptionSchema>;

export const homeFormSchema = z.object({
  enabled: z.boolean(),
  show_to: z.enum(HOME_AUDIENCES),
  greeting: z.string().trim().max(L.greeting),
  subtitle: z.string().trim().max(L.subtitle),
  options: z.array(homeOptionSchema).max(L.options),
  messenger: z.object({
    get_started: z.boolean(),
    greeting: z.string().trim().max(L.messengerGreeting),
    persistent_menu: z.boolean(),
  }),
});
export type HomeForm = z.infer<typeof homeFormSchema>;

type Raw = Record<string, unknown>;
const record = (value: unknown): Raw => (value && typeof value === "object" && !Array.isArray(value) ? (value as Raw) : {});
const text = (value: unknown, max: number): string => (typeof value === "string" ? value.slice(0, max) : "");
const flag = (value: unknown, fallback: boolean): boolean => (typeof value === "boolean" ? value : fallback);
function pick<T extends string>(value: unknown, options: readonly T[], fallback: T): T {
  return options.find((option) => option === value) ?? fallback;
}

function optionForm(value: unknown): HomeOptionForm | null {
  const raw = record(value);
  const id = text(raw.id, L.optionId);
  const label = text(raw.label, L.label);
  const action = HOME_ACTIONS.find((candidate) => candidate === raw.action);
  if (!id || !label.trim() || !action) return null;
  return {
    id,
    icon: pick(raw.icon, HOME_ICONS, HOME_DEFAULT_ICON),
    label,
    description: text(raw.description, L.description),
    action,
    workflow_id: text(raw.workflow_id, L.optionId * 2),
    action_id: text(raw.action_id, L.optionId * 2),
    question: text(raw.question, L.question),
    url: text(raw.url, L.url),
    audience: pick(raw.audience, HOME_AUDIENCES, HOME_DEFAULT_AUDIENCE),
  };
}

/** `draft.content.home` (loosely typed server data) → complete form values; Home is off and empty by default. */
export function toHomeForm(value: unknown): HomeForm {
  const raw = record(value);
  const messenger = record(raw.messenger);
  const options = Array.isArray(raw.options) ? raw.options.flatMap((entry) => optionForm(entry) ?? []).slice(0, L.options) : [];
  return {
    enabled: flag(raw.enabled, false),
    show_to: pick(raw.show_to, HOME_AUDIENCES, HOME_DEFAULT_AUDIENCE),
    greeting: text(raw.greeting, L.greeting),
    subtitle: text(raw.subtitle, L.subtitle),
    options,
    messenger: {
      get_started: flag(messenger.get_started, false),
      greeting: text(messenger.greeting, L.messengerGreeting),
      persistent_menu: flag(messenger.persistent_menu, false),
    },
  };
}

/** The generated `content.home` of the widget draft and one of its options. */
export type HomeDraft = NonNullable<WidgetDraftIn["content"]["home"]>;
type HomeDraftOption = NonNullable<HomeDraft["options"]>[number];

/** Only what the chosen action uses is sent: a link option carries a url and nothing else (the server defaults a missing description to blank). */
function optionDraft(option: HomeOptionForm): HomeDraftOption {
  const base: HomeDraftOption = { id: option.id, icon: option.icon, label: option.label.trim(), description: option.description.trim(), action: option.action, audience: option.audience };
  if (option.action === "workflow") base.workflow_id = option.workflow_id;
  if (option.action === "booking") base.action_id = option.action_id;
  if (option.action === "question") base.question = option.question.trim();
  if (option.action === "link") base.url = option.url.trim();
  return base;
}

/** Form → `content.home` of `PUT /v1/agents/{id}/widget`. */
export function toHomeDraft(form: HomeForm): HomeDraft {
  return {
    enabled: form.enabled,
    show_to: form.show_to,
    greeting: form.greeting.trim(),
    subtitle: form.subtitle.trim(),
    options: form.options.map(optionDraft),
    messenger: { get_started: form.messenger.get_started, greeting: form.messenger.greeting.trim(), persistent_menu: form.messenger.persistent_menu },
  };
}

/** Ids the server keeps for itself: `start` is the Messenger Get Started payload (`sagot:home:start`). */
const RESERVED_OPTION_IDS: readonly string[] = [HOME_RESERVED_OPTION_ID];

function slug(label: string): string {
  return label
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, ID_SLUG_MAX)
    .replace(/-+$/g, "");
}

/**
 * A stable id for a new option (`^[a-z0-9][a-z0-9_-]{0,39}$`, never `start`): the label's slug,
 * numbered when taken. It never changes when the label is edited later, so tap counts stay attached.
 */
export function newOptionId(label: string, taken: readonly string[]): string {
  const unavailable = (id: string) => taken.includes(id) || RESERVED_OPTION_IDS.includes(id);
  const base = slug(label) || "option";
  if (!unavailable(base)) return base;
  for (let n = 2; n <= taken.length + RESERVED_OPTION_IDS.length + 2; n += 1) {
    const candidate = `${base}-${String(n)}`;
    if (!unavailable(candidate)) return candidate;
  }
  return `${base}-${String(taken.length + RESERVED_OPTION_IDS.length + 3)}`;
}

export function emptyOption(): HomeOptionForm {
  return { id: "", icon: HOME_DEFAULT_ICON, label: "", description: "", action: "workflow", workflow_id: "", action_id: "", question: "", url: "", audience: HOME_DEFAULT_AUDIENCE };
}

/** First error code per field of an option (the dialog shows them inline). */
export function optionErrors(option: HomeOptionForm): Partial<Record<keyof HomeOptionForm, HomeErrorCode>> {
  const result = homeOptionSchema.safeParse({ ...option, id: option.id || "pending" });
  if (result.success) return {};
  const errors: Partial<Record<keyof HomeOptionForm, HomeErrorCode>> = {};
  for (const issue of result.error.issues) {
    const field = issue.path[0] as keyof HomeOptionForm | undefined;
    if (field && !errors[field]) errors[field] = isErrorCode(issue.message) ? issue.message : "invalid";
  }
  return errors;
}

export function reorder<T>(list: readonly T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return [...list];
  const next = [...list];
  const [moved] = next.splice(from, 1);
  if (moved !== undefined) next.splice(to, 0, moved);
  return next;
}

const SYNC_STATUSES = ["synced", "removed", "failed", "skipped"] as const;
export type MessengerSyncStatus = (typeof SYNC_STATUSES)[number];

/** `WidgetOut.messenger_profile`: what Sagot last pushed to the agent's connected Facebook Page. */
export interface MessengerProfile {
  pageName: string;
  /** `null`: never pushed. */
  status: MessengerSyncStatus | null;
  /** A fixed, safe sentence from the server (never provider text). */
  error: string | null;
  syncedAt: string | null;
}

const optionalText = (value: unknown): string | null => (typeof value === "string" && value ? value : null);

/**
 * The Messenger Profile of the widget payload (`null` = no connected Page). The server sends the
 * status as free text; an unknown one is shown as "never pushed".
 */
export function readMessengerProfile(profile: WidgetOut["messenger_profile"]): MessengerProfile | null {
  const raw = record(profile);
  if (typeof raw.page_name !== "string") return null;
  return {
    pageName: raw.page_name,
    status: SYNC_STATUSES.find((status) => status === raw.status) ?? null,
    error: optionalText(raw.error),
    syncedAt: optionalText(raw.synced_at),
  };
}
